import { normalizarTexto } from '../utils/textoBusqueda';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../contexts/ToastContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, HardDrive, Filter, Trash2, FileCheck, CheckCircle, XCircle, AlertCircle, List as ListIcon } from 'lucide-react';
import { FileText, Search, ChevronLeft, ChevronRight, Eye, Upload, History, User, UserCheck, LayoutGrid } from '../components/icons';
import { asignacionesAPI, actasAPI, consumiblesAPI } from '../services/api';
import GenerarActaModal from '../components/GenerarActaModal';
import SeleccionarAsignacionActaModal from '../components/SeleccionarAsignacionActaModal';
import StatusBadge from '../components/StatusBadge';
import { indexarActa, buscarActaDeAsignacion } from '../utils/actasMatch';
import UploadHistoricoModal from '../components/UploadHistoricoModal';
import SignatureDrawer from '../components/SignatureDrawer';
// Eliminado TestNotifications: ahora las notificaciones se gestionan desde el detalle de actas

const GestionActas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // "Generar Nueva Acta" del menú lateral: /gestion-actas?action=generar abre el selector de asignación
  const [showSelectorActa, setShowSelectorActa] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('action') === 'generar') {
      setShowSelectorActa(true);
      navigate('/gestion-actas', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const { showToast } = useToast();

  const handleRowClick = (id) => {
    if (!id) return;
    navigate(`/gestion-actas/${id}`);
  };

  // Estados principales
  const [asignaciones, setAsignaciones] = useState([]);
  const [actasPorAsignacion, setActasPorAsignacion] = useState({}); // Mapeo de actas por asignación
  const [loading, setLoading] = useState(true);
  const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const handleSaveSignatureFromDrawer = async (signatureFile) => {
    try {
      const formData = new FormData();
      formData.append('firma', signatureFile);
      await actasAPI.subirFirma(formData);
      showToast('Éxito', 'Firma digital registrada correctamente en tu perfil', 'success');
      setShowSignatureDrawer(false);

      if (pendingAction) {
        const actionToRetry = pendingAction;
        setPendingAction(null);
        await actionToRetry();
      }
    } catch (err) {
      console.error('Error guardando firma:', err);
      showToast('Error', 'No se pudo guardar la firma digital', 'error');
    }
  };

  const checkSignatureErrorAndPrompt = (error, actionCallback) => {
    const isNoSig =
      error.response?.data?.code === 'NO_SIGNATURE' ||
      (error.response?.status === 400 && (error.response?.data?.message || '').toLowerCase().includes('firma'));

    if (isNoSig) {
      const msg = error.response?.data?.message || 'No tienes una firma digital cargada en tu perfil. Por favor dibuja tu firma a continuación.';
      showToast('Firma Requerida', msg, 'info');
      if (actionCallback) setPendingAction(() => actionCallback);
      setShowSignatureDrawer(true);
      return true;
    }
    return false;
  };

  // Estados de filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterCategoria, setFilterCategoria] = useState('todos');
  const [filterEstadoActa, setFilterEstadoActa] = useState('todos'); // 'todos', 'pendiente', 'aprobada', 'rechazada'

  // Estados para ordenamiento
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Estados de modales
  const [showGenerarActaModal, setShowGenerarActaModal] = useState(false);
  const [selectedAsignacion, setSelectedAsignacion] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedAsignacionForUpload, setSelectedAsignacionForUpload] = useState(null);
  const [observaciones, setObservaciones] = useState('');

  // Estados para modal de previsualización personalizada
  const [showPrevisualizacionModal, setShowPrevisualizacionModal] = useState(false);
  const [selectedActaForPreview, setSelectedActaForPreview] = useState(null);
  const [selectedAsignacionForPreview, setSelectedAsignacionForPreview] = useState(null);
  const [incluirFirmaTI, setIncluirFirmaTI] = useState(true);
  const [fechaEntrega, setFechaEntrega] = useState('');

  // Estados para Control de Actas
  const [actas, setActas] = useState([]);
  const [loadingActas, setLoadingActas] = useState(true);
  // Hasta que no llega el mapa de actas, todas las asignaciones parecerían "Sin acta": no se muestra el listado antes
  const [actasMapaListo, setActasMapaListo] = useState(false);
  const [searchTermActas, setSearchTermActas] = useState('');
  const [filterStatusActas, setFilterStatusActas] = useState('todos');
  const [sortOrderActas, setSortOrderActas] = useState('fecha-desc'); // 'fecha-desc', 'fecha-asc', 'usuario', 'activo', 'estado'
  const [showUploadModalActas, setShowUploadModalActas] = useState(false);
  const [uploadDataActas, setUploadDataActas] = useState({
    usuarioId: '',
    nombreArchivo: '',
    descripcion: ''
  });
  const [selectedFileActas, setSelectedFileActas] = useState(null);

  // Estado para modal histórico
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);

  // Estado para controlar la pestaña activa
  const [activeTab, setActiveTab] = useState('asignaciones'); // 'asignaciones' o 'control-actas'

  // Estados de vista (Persistencia)
  const [viewModeAsignaciones, setViewModeAsignaciones] = useState(() => {
    return localStorage.getItem('gestionAsignacionesViewMode') || 'grid';
  });
  const [viewModeControl, setViewModeControl] = useState(() => {
    return localStorage.getItem('gestionControlActasViewMode') || 'grid';
  });

  useEffect(() => {
    localStorage.setItem('gestionAsignacionesViewMode', viewModeAsignaciones);
  }, [viewModeAsignaciones]);

  useEffect(() => {
    localStorage.setItem('gestionControlActasViewMode', viewModeControl);
  }, [viewModeControl]);

  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalAsignaciones: 0,
    asignacionesActivas: 0,
    asignacionesEsteMes: 0,
    actasPendientes: 0,
    actasFirmadas: 0,
    actasAprobadas: 0,
    actasRechazadas: 0,
    porcentajeCompletado: 0,
    actasSubidas: 0
  });

  // Estado para asignaciones sin acta
  const [asignacionesSinActa, setAsignacionesSinActa] = useState([]);

  const fetchAsignaciones = useCallback(async () => {
    try {
      setLoading(true);
      // Cargar asignaciones de activos
      const { data: asignacionesData } = await asignacionesAPI.getActivas();

      // Cargar salidas de consumibles (que actúan como asignaciones)
      const { data: consumiblesData } = await consumiblesAPI.getSalidas();

      // Normalizar consumibles para asegurar que tengan la estructura esperada
      // Necesitamos que parezcan 'asignaciones' para la tabla
      const consumiblesNormalizados = consumiblesData.map(c => ({
        id: `mov_${c.id}`, // ID prefijado para evitar colisión
        originalId: c.id,
        tipo: 'consumible',
        fechaAsignacion: c.fecha,
        estado: 'Activa', // Asumimos activa si fue entregado
        activo: {
          id: c.consumible.id, // ID del consumible
          codigo: 'CONSUMIBLE', // Código genérico o usar ID
          categoria: 'Consumibles', // Categoría fija
          nombre: c.consumible.nombre, // Nombre del consumible
          modelo: c.observaciones || '', // Usamos observaciones como detalle extra
          marca: ''
        },
        usuario: c.usuario, // Usuario destinatario
        acta: c.actas && c.actas.length > 0 ? c.actas[0] : null // Adjuntar acta si existe
      }));

      // Combinar ambas listas
      setAsignaciones([...asignacionesData, ...consumiblesNormalizados]);
    } catch (error) {
      console.error('Error al cargar asignaciones:', error);
      showToast('Error', 'No se pudieron cargar las asignaciones', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Cargar actas pendientes de aprobación y actualizar mapa completo
  const fetchActasPendientes = useCallback(async () => {
    try {
      setLoadingActas(true);

      // Obtener actas pendientes de aprobación para estadísticas
      const { data: actasPendientesAprobacion } = await actasAPI.getActasPendientesAprobacion();

      // Obtener TODAS las actas para el mapa completo
      const { data: todasActas } = await actasAPI.getTodasActas();

      // Mapa de TODAS las actas: por asignación/movimiento y, para las históricas, por equipo + persona
      // (por usuarioId o, si el acta solo trae el nombre, por nombre).
      const actasMap = {};
      todasActas.forEach(acta => indexarActa(actasMap, acta));

      setActasPorAsignacion(actasMap);
      setActas(actasPendientesAprobacion);

      // Calcular estadísticas basadas en TODAS las actas
      const norm = (s) => (s || '').toLowerCase();

      const pendientes = todasActas.filter(a => {
        const e = norm(a.estado);
        return e === 'pendiente' || e === 'pendiente de aprobación' || e === 'pendiente_de_aprobacion';
      }).length;

      const aprobadas = todasActas.filter(a => {
        const e = norm(a.estado);
        return e === 'aprobada' || e === 'firmada';
      }).length;

      const rechazadas = todasActas.filter(a => norm(a.estado) === 'rechazada').length;

      setStats(prev => ({
        ...prev,
        actasPendientes: pendientes,
        actasAprobadas: aprobadas,
        actasRechazadas: rechazadas,
        actasSubidas: aprobadas // Solo mostrar actas firmadas/aprobadas
      }));
    } catch (error) {
      console.error('Error cargando actas pendientes:', error);
      showToast('Error', 'No se pudieron cargar las actas pendientes', 'error');
    } finally {
      setLoadingActas(false);
      setActasMapaListo(true);
    }
  }, [showToast]);

  // Funciones para Control de Actas
  const fetchActas = useCallback(async () => {
    try {
      setLoadingActas(true);
      const { data } = await actasAPI.getTodasActas();

      // Mostrar solo actas firmadas (las únicas que tienen PDF válido)
      const actasFirmadas = data.filter(acta =>
        acta.estado?.toLowerCase() === 'firmada'
      );
      setActas(actasFirmadas);
    } catch (error) {
      showToast('Error', 'Error al cargar las actas', 'error');
      console.error('Error:', error);
    } finally {
      setLoadingActas(false);
    }
  }, [showToast]);

  const handleFileChangeActas = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFileActas(file);
    } else {
      showToast('Error', 'Solo se permiten archivos PDF', 'error');
      e.target.value = null;
    }
  };

  const handleUploadActas = async (e) => {
    e.preventDefault();

    if (!uploadDataActas.usuarioId || !uploadDataActas.nombreArchivo || !selectedFileActas) {
      showToast('Error', 'Por favor completa todos los campos y selecciona un archivo', 'error');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('acta', selectedFileActas);
      formData.append('usuarioId', uploadDataActas.usuarioId);
      formData.append('nombreArchivo', uploadDataActas.nombreArchivo);
      formData.append('descripcion', uploadDataActas.descripcion);

      await actasAPI.subirActaAdmin(formData);

      showToast('Éxito', 'Acta subida exitosamente', 'success');
      setShowUploadModalActas(false);
      setUploadDataActas({ usuarioId: '', nombreArchivo: '', descripcion: '' });
      setSelectedFileActas(null);
      fetchActas();
    } catch (error) {
      showToast('Error', 'Error al subir el acta', 'error');
      console.error('Error:', error);
    }
  };

  const handlePreview = async (actaId, nombreArchivo) => {
    try {
      const { data } = await actasAPI.descargarActa(actaId);
      const url = window.URL.createObjectURL(data);
      window.open(url, '_blank');
    } catch (error) {
      showToast('Error', 'Error al previsualizar el acta', 'error');
      console.error('Error:', error);
    }
  };

  const handleDelete = async (actaId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este acta?')) {
      return;
    }

    try {
      await actasAPI.eliminarActa(actaId);
      showToast('Éxito', 'Acta eliminada exitosamente', 'success');
      fetchActas();
    } catch (error) {
      showToast('Error', 'Error al eliminar el acta', 'error');
      console.error('Error:', error);
    }
  };

  const sortActas = (actasToSort) => {
    const sortedActas = [...actasToSort];

    switch (sortOrderActas) {
      case 'fecha-desc':
        return sortedActas.sort((a, b) => new Date(b.fechaSubida || b.fechaCreacion) - new Date(a.fechaSubida || a.fechaCreacion));
      case 'fecha-asc':
        return sortedActas.sort((a, b) => new Date(a.fechaSubida || a.fechaCreacion) - new Date(b.fechaSubida || b.fechaCreacion));
      case 'usuario':
        return sortedActas.sort((a, b) => {
          const usuarioA = a.asignacion?.usuario || a.movimiento?.usuario;
          const usuarioB = b.asignacion?.usuario || b.movimiento?.usuario;
          const userA = `${usuarioA?.nombre || ''} ${usuarioA?.apellido || ''}`.toLowerCase();
          const userB = `${usuarioB?.nombre || ''} ${usuarioB?.apellido || ''}`.toLowerCase();
          return userA.localeCompare(userB);
        });
      case 'activo':
        return sortedActas.sort((a, b) => {
          const itemA = a.asignacion?.activo || a.movimiento?.consumible;
          const itemB = b.asignacion?.activo || b.movimiento?.consumible;
          const labelA = (itemA?.codigo || itemA?.nombre || '').toLowerCase();
          const labelB = (itemB?.codigo || itemB?.nombre || '').toLowerCase();
          return labelA.localeCompare(labelB);
        });
      case 'estado':
        return sortedActas.sort((a, b) => {
          const estadoA = a.estado?.toLowerCase() || '';
          const estadoB = b.estado?.toLowerCase() || '';
          return estadoA.localeCompare(estadoB);
        });
      default:
        return sortedActas;
    }
  };

  const getStatusColorActas = (status) => {
    switch (status) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'firmado': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rechazado': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Función para identificar asignaciones sin acta
  const identificarAsignacionesSinActa = useCallback(async () => {
    if (asignaciones.length === 0) {
      setAsignacionesSinActa([]);
      return;
    }

    try {
      // Obtener todas las actas para verificar cuáles asignaciones tienen acta
      const { data: todasActas } = await actasAPI.getTodasActas();

      // Mismo mapa que usa la tarjeta, para que el contador y cada tarjeta coincidan
      const todasActasMap = {};
      todasActas.forEach(acta => indexarActa(todasActasMap, acta));

      // Filtrar asignaciones que NO tienen acta (ni directa ni histórica del mismo equipo+persona)
      const sinActa = asignaciones.filter(
        (asignacion) => !buscarActaDeAsignacion(asignacion, todasActasMap),
      );

      setAsignacionesSinActa(sinActa);
    } catch (error) {
      console.error('Error al identificar asignaciones sin acta:', error);
      setAsignacionesSinActa([]);
    }
  }, [asignaciones]);

  // Función para marcar asignaciones sin acta como pendientes de firma
  const handleMarcarPendientesFirma = async () => {
    if (asignacionesSinActa.length === 0) {
      showToast('Información', 'No hay asignaciones sin acta para marcar', 'info');
      return;
    }

    const confirmMessage = `¿Estás seguro de que quieres marcar ${asignacionesSinActa.length} asignación(es) como pendientes de firma?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);

      // Crear actas pendientes para cada asignación sin acta
      const promesas = asignacionesSinActa.map(async (asignacion) => {
        try {
          let payload = {
            observaciones: 'Acta generada automáticamente - Pendiente de firma'
          };

          const idStr = String(asignacion.id);
          if (idStr.startsWith('mov_')) {
            payload.movimientoId = parseInt(idStr.replace('mov_', ''));
          } else {
            payload.asignacionId = parseInt(idStr);
          }

          await actasAPI.marcarPendienteFirma(payload);
        } catch (error) {
          console.error(`Error al crear acta para asignación ${asignacion.id}:`, error);
          throw error;
        }
      });

      await Promise.all(promesas);

      showToast('Éxito', `${asignacionesSinActa.length} asignación(es) marcadas como pendientes de firma`, 'success');

      // Recargar datos para actualizar la vista
      await Promise.all([
        fetchAsignaciones(),
        fetchActasPendientes(),
        identificarAsignacionesSinActa()
      ]);

    } catch (error) {
      console.error('Error al marcar asignaciones como pendientes:', error);
      showToast('Error', 'No se pudieron marcar todas las asignaciones como pendientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Funciones de filtrado para Control de Actas
  const filteredActas = sortActas(actas.filter(acta => {
    const searchTerm = searchTermActas.toLowerCase();
    const usuario = acta.asignacion?.usuario || acta.movimiento?.usuario;
    const matchesSearch =
      (acta.nombreArchivo?.toLowerCase() || '').includes(searchTerm) ||
      (usuario?.nombre?.toLowerCase() || '').includes(searchTerm) ||
      (usuario?.apellido?.toLowerCase() || '').includes(searchTerm) ||
      (usuario?.email?.toLowerCase() || '').includes(searchTerm);
    const matchesFilter = filterStatusActas === 'todos' || acta.estado === filterStatusActas;
    return matchesSearch && matchesFilter;
  }));

  useEffect(() => {
    let mounted = true;

    const cargarDatos = async () => {
      if (!mounted) return;

      try {
        await fetchAsignaciones();
        if (mounted) {
          await fetchActasPendientes();
        }
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
      }
    };

    cargarDatos();

    // Recargar datos cada 10 minutos para evitar sobrecarga del servidor
    const interval = setInterval(async () => {
      if (!mounted) return;

      try {
        await fetchAsignaciones();
        if (mounted) {
          await fetchActasPendientes();
        }
      } catch (error) {
        console.error('Error recargando datos:', error);
      }
    }, 600000); // 10 minutos

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []); // Sin dependencias para evitar re-ejecuciones

  // Cargar actas cuando se active la pestaña de Control de Actas
  useEffect(() => {
    if (activeTab === 'control-actas') {
      fetchActas();
    }
  }, [activeTab, fetchActas]);

  // Calcular estadísticas cuando cambien los datos
  useEffect(() => {
    const calcularEstadisticas = () => {
      const totalAsignaciones = asignaciones.length;
      const asignacionesActivas = asignaciones.filter(a => a.estado === 'Activa').length;
      const asignacionesEsteMes = asignaciones.filter(a => {
        const asignacionDate = new Date(a.fechaAsignacion);
        const now = new Date();
        return asignacionDate.getMonth() === now.getMonth() &&
          asignacionDate.getFullYear() === now.getFullYear();
      }).length;

      // Calcular porcentaje completado basándose en asignaciones activas con actas aprobadas
      const asignacionesConActaAprobada = asignacionesActivas > 0 ?
        asignaciones.filter(a => {
          const acta = buscarActaDeAsignacion(a, actasPorAsignacion);
          const e = acta?.estado?.toLowerCase();
          return a.estado === 'Activa' && (e === 'aprobada' || e === 'firmada');
        }).length : 0;

      const porcentajeCompletado = asignacionesActivas > 0
        ? Math.round((asignacionesConActaAprobada / asignacionesActivas) * 100)
        : 0;

      setStats(prev => ({
        ...prev,
        totalAsignaciones,
        asignacionesActivas,
        asignacionesEsteMes,
        porcentajeCompletado
      }));
    };

    calcularEstadisticas();
  }, [asignaciones, actasPorAsignacion]);

  // Identificar asignaciones sin acta cuando cambien los datos
  useEffect(() => {
    identificarAsignacionesSinActa();
  }, [identificarAsignacionesSinActa]);

  const handleAprobarActa = async (actaId, aprobado, comentarios = '') => {
    try {
      await actasAPI.aprobarActa(actaId, { aprobar: aprobado, comentarios: comentarios });
      showToast('Éxito', `Acta ${aprobado ? 'aprobada' : 'rechazada'} correctamente`, 'success');
      // Recargar asignaciones para actualizar el estado
      fetchAsignaciones();
      // También recargar actas pendientes
      fetchActasPendientes();
    } catch (error) {
      console.error('Error al procesar acta:', error);
      showToast('Error', 'No se pudo procesar la acta', 'error');
    }
  };



  // 🎯 FLUJO COMPLETO - 4 CONDICIONES DEL OJO NARANJA
  const handlePrevisualizarActa = async (acta, idGenerico) => {
    try {
      let response;
      let asignacionId = null;
      let movimientoId = null;

      // Determinar si es asignación o movimiento basado en el ID genérico
      if (typeof idGenerico === 'string' && idGenerico.startsWith('mov_')) {
        movimientoId = parseInt(idGenerico.replace('mov_', ''));
      } else {
        asignacionId = parseInt(idGenerico);
      }

      // 1. 🆕 PENDIENTE DE FIRMA - !acta (no existe acta)
      if (!acta) {
        if (movimientoId) {
          response = await actasAPI.previsualizarMovimiento(movimientoId);
        } else {
          response = await actasAPI.previsualizarActa(asignacionId);
        }
        showToast('Información', 'Generando acta sin firma para el usuario', 'info');
      }
      // 2. ⏳ PENDIENTE DE APROBACIÓN
      else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'pdf_subido') {
        response = await actasAPI.descargarActa(acta.id);
        showToast('Información', 'Revisando PDF subido por el usuario', 'info');
      }
      // 3. ✍️ FIRMADA DIGITALMENTE
      else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'digital') {
        response = await actasAPI.previsualizarActaFirmado(acta.id);
        showToast('Información', 'Revisando acta con firma digital del usuario', 'info');
      }
      // 4. 📤 SUBIDA POR ADMIN/SOPORTE
      else if (acta.metodoFirma?.toLowerCase() === 'admin_subida') {
        response = await actasAPI.descargarActa(acta.id);
        showToast('Información', 'Revisando acta que subiste', 'info');
      }
      // Caso por defecto
      else {
        if (movimientoId) {
          response = await actasAPI.previsualizarMovimiento(movimientoId);
        } else {
          response = await actasAPI.previsualizarActa(asignacionId);
        }
        showToast('Información', 'Previsualizando acta', 'info');
      }

      if (response) {
        const url = window.URL.createObjectURL(response.data);
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudo previsualizar el acta', 'error');
    }
  };



  const handleCrearActasPendientes = async () => {
    try {
      const response = await asignacionesAPI.crearActasPendientes();
      showToast('Éxito', `Se crearon ${response.data.actasCreadas} actas pendientes`, 'success');
      // Recargar los datos
      fetchAsignaciones();
      fetchActasPendientes();
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudieron crear las actas pendientes', 'error');
    }
  };

  const handleMarcarPendienteFirma = async (idGenerico) => {
    try {
      let payload = {};
      if (typeof idGenerico === 'string' && idGenerico.startsWith('mov_')) {
        payload.movimientoId = parseInt(idGenerico.replace('mov_', ''));
      } else {
        payload.asignacionId = parseInt(idGenerico);
      }

      await actasAPI.marcarPendienteFirma(payload);
      showToast('Éxito', 'Acta marcada como pendiente de firma exitosamente', 'success');
      // Recargar los datos
      fetchAsignaciones();
      fetchActasPendientes();
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudo marcar como pendiente', 'error');
    }
  };

  const handleDeleteMovimiento = async (idGenerico) => {
    if (!window.confirm('¿Estás seguro de eliminar este insumo asignado? Esta acción devolverá el stock.')) return;
    try {
      const movimientoId = parseInt(idGenerico.replace('mov_', ''));
      await consumiblesAPI.deleteMovimiento(movimientoId);
      showToast('Éxito', 'Insumo eliminado y stock restaurado', 'success');
      fetchAsignaciones();
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'Error al eliminar el insumo', 'error');
    }
  };


  // Funciones de manejo de actas
  const handleGenerarActa = (acta, asignacionId) => {
    // Buscar la asignación completa por ID
    if (!asignacionId) {
      showToast('Error', 'No se ha seleccionado una asignación válida', 'error');
      return;
    }

    // Manejar IDs de tipo string (mov_X) o numéricos
    let asignacionCompleta;
    const asignacionIdStr = String(asignacionId);

    asignacionCompleta = asignaciones.find(a => String(a.id) === asignacionIdStr);

    if (!asignacionCompleta) {
      // Intentar construcción manual si es mov_
      if (asignacionIdStr.startsWith('mov_')) {
        const movId = parseInt(asignacionIdStr.replace('mov_', ''));
        asignacionCompleta = { id: asignacionIdStr, originalId: movId, tipo: 'consumible' };
      } else {
        showToast('Error', 'No se encontró la asignación', 'error');
        return;
      }
    }

    setSelectedAsignacion(asignacionCompleta);
    setShowGenerarActaModal(true);
  };

  const handleSubirActa = (acta, idGenerico) => {
    if (!idGenerico) {
      showToast('Error', 'No se ha seleccionado una asignación válida', 'error');
      return;
    }

    let asignacionCompleta;
    let asignacionIdStr = String(idGenerico);

    // Buscar en la lista de asignaciones cargada
    asignacionCompleta = asignaciones.find(a => String(a.id) === asignacionIdStr);

    // Si no se encuentra (raro si viene de la tabla), intentar construir objeto mínimo
    if (!asignacionCompleta) {
      // Determinar si es movimiento o asignación
      if (asignacionIdStr.startsWith('mov_')) {
        const movId = parseInt(asignacionIdStr.replace('mov_', ''));
        asignacionCompleta = { id: asignacionIdStr, originalId: movId, tipo: 'consumible' };
      } else {
        const asigId = parseInt(asignacionIdStr);
        asignacionCompleta = { id: asigId, tipo: 'asignacion' };
      }
    }

    setSelectedAsignacionForUpload(asignacionCompleta);
    setShowUploadModal(true);
  };

  const handleCloseGenerarActa = () => {
    setShowGenerarActaModal(false);
    setSelectedAsignacion(null);
  };

  const handleGenerarActaSuccess = () => {
    showToast('Éxito', 'Acta generada correctamente', 'success');
    handleCloseGenerarActa();
    fetchActasPendientes();
  };



  const handleMarcarPendiente = async (idGenerico) => {
    if (window.confirm('¿Estás seguro de que deseas marcar esta acta como pendiente de firma?\\nEsto permitirá al usuario volver a iniciar el proceso de firma.')) {
      try {
        setLoading(true);

        let payload = {
          observaciones: 'Marcado como pendiente manualmente por administrador'
        };

        if (typeof idGenerico === 'string' && idGenerico.startsWith('mov_')) {
          payload.movimientoId = parseInt(idGenerico.replace('mov_', ''));
        } else {
          payload.asignacionId = parseInt(idGenerico);
        }

        await actasAPI.marcarPendienteFirma(payload);
        showToast('Éxito', 'El acta ha sido marcada como pendiente correctamente', 'success');

        // Recargar datos
        await Promise.all([
          fetchAsignaciones(),
          fetchActasPendientes()
        ]);

        // Si la pestaña de control de actas está activa, recargar también
        if (activeTab === 'control-actas') {
          fetchActas();
        }
      } catch (error) {
        console.error('Error al marcar como pendiente:', error);
        if (!checkSignatureErrorAndPrompt(error, () => handleMarcarPendiente(idGenerico))) {
          showToast('Error', 'No se pudo marcar el acta como pendiente', 'error');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubirActaUsuario = (asignacion) => {
    setSelectedAsignacionForUpload(asignacion);
    setShowUploadModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      showToast('Error', 'Solo se permiten archivos PDF', 'error');
      e.target.value = null;
    }
  };

  const handleUploadActa = async () => {
    if (!selectedFile || !selectedAsignacionForUpload) {
      showToast('Error', 'Selecciona un archivo y una asignación', 'error');
      return;
    }

    const asignacionIdVal = selectedAsignacionForUpload.id;
    const isMovimiento = selectedAsignacionForUpload.tipo === 'consumible';

    // Validar ID numérico si es asignación normal, o ID string numérico si es movimiento
    if (!asignacionIdVal) {
      showToast('Error', 'La asignación seleccionada no es válida', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('acta', selectedFile);

    if (isMovimiento) {
      // Si es consumo, enviamos movimientoId (originalId)
      formData.append('movimientoId', selectedAsignacionForUpload.originalId.toString());
    } else {
      // Si es activo, enviamos asignacionId
      formData.append('asignacionId', asignacionIdVal.toString());
    }

    formData.append('observaciones', observaciones || '');

    try {
      await actasAPI.subirActaAdmin(formData);
      showToast('Éxito', 'Acta subida correctamente', 'success');
      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedAsignacionForUpload(null);
      setObservaciones('');
      fetchActasPendientes(); // Recargar lista
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudo subir el archivo', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Función para manejar previsualización personalizada
  const handlePrevisualizacionPersonalizada = (acta, asignacion) => {
    setSelectedActaForPreview(acta);
    setSelectedAsignacionForPreview(asignacion);
    setIncluirFirmaTI(true);
    setFechaEntrega(new Date().toISOString().split('T')[0]); // Fecha actual por defecto
    setShowPrevisualizacionModal(true);
  };

  const handleConfirmarPrevisualizacion = async () => {
    try {
      let response;

      if (selectedActaForPreview) {
        // Si hay acta, usar previsualización con parámetros personalizados
        response = await actasAPI.previsualizarActaPersonalizada(selectedActaForPreview.id, {
          incluirFirmaTI: incluirFirmaTI,
          fechaEntrega: fechaEntrega
        });
      } else {
        // Si no hay acta, generar nueva con parámetros personalizados
        response = await actasAPI.previsualizarActaPersonalizada(selectedAsignacionForPreview.id, {
          incluirFirmaTI: incluirFirmaTI,
          fechaEntrega: fechaEntrega
        });
      }

      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');

      setShowPrevisualizacionModal(false);
      setSelectedActaForPreview(null);
      setSelectedAsignacionForPreview(null);
    } catch (error) {
      console.error('Error:', error);
      if (!checkSignatureErrorAndPrompt(error, handleConfirmarPrevisualizacion)) {
        showToast('Error', 'No se pudo generar la previsualización personalizada', 'error');
      }
    }
  };





  // Función de ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Función para obtener el valor de ordenamiento
  const getSortValue = (asignacion, field) => {
    switch (field) {
      case 'codigo':
        return asignacion.activo?.codigo || '';
      case 'categoria':
        return asignacion.activo?.categoria || '';
      case 'usuario':
        return `${asignacion.usuario?.nombre || ''} ${asignacion.usuario?.apellido || ''}`;
      case 'estado':
        return asignacion.estado || '';
      case 'fecha':
        return asignacion.fechaAsignacion || '';
      default:
        return '';
    }
  };

  // Filtros y búsqueda
  const filteredAsignaciones = asignaciones.filter(asignacion => {
    const matchesSearch =
      normalizarTexto(asignacion.activo?.codigo).includes(normalizarTexto(searchTerm)) ||
      normalizarTexto(asignacion.activo?.categoria).includes(normalizarTexto(searchTerm)) ||
      normalizarTexto(asignacion.usuario?.nombre).includes(normalizarTexto(searchTerm)) ||
      normalizarTexto(asignacion.usuario?.apellido).includes(normalizarTexto(searchTerm)) ||
      normalizarTexto(asignacion.usuario?.email).includes(normalizarTexto(searchTerm));

    const matchesStatus = filterStatus === 'todos' || asignacion.estado === filterStatus;
    const matchesCategoria = filterCategoria === 'todos' || asignacion.activo?.categoria === filterCategoria;

    // Obtener estado del acta (incluye actas históricas por activo+usuario)
    const acta = buscarActaDeAsignacion(asignacion, actasPorAsignacion);
    const estadoActa = acta?.estado?.toLowerCase();

    // Ocultar automáticamente actas firmadas y aprobadas (excepto si se filtran explícitamente)
    if (filterEstadoActa === 'todos') {
      // Si no hay filtro específico, ocultar firmadas y aprobadas
      if (estadoActa === 'firmada' || estadoActa === 'aprobada') {
        return false;
      }
    }

    // Filtro por estado de acta
    let matchesEstadoActa = true;
    if (filterEstadoActa !== 'todos') {
      if (filterEstadoActa === 'pendiente') {
        // Pendientes: estado 'pendiente' o 'pendiente de aprobación'
        matchesEstadoActa = acta && (estadoActa === 'pendiente' || estadoActa === 'pendiente de aprobación' || estadoActa === 'pendiente_de_aprobacion');
      } else if (filterEstadoActa === 'aprobada') {
        // Aprobadas: estado 'aprobada' o 'firmada'
        matchesEstadoActa = acta && (estadoActa === 'aprobada' || estadoActa === 'firmada');
      } else if (filterEstadoActa === 'rechazada') {
        // Rechazadas: estado 'rechazada'
        matchesEstadoActa = acta && estadoActa === 'rechazada';
      }
    }

    return matchesSearch && matchesStatus && matchesCategoria && matchesEstadoActa;
  });

  // Aplicar ordenamiento
  const sortedAsignaciones = [...filteredAsignaciones].sort((a, b) => {
    if (!sortField) return 0;

    const aValue = getSortValue(a, sortField);
    const bValue = getSortValue(b, sortField);

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAsignaciones = sortedAsignaciones.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedAsignaciones.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Funciones de utilidad
  const getStatusColor = (status) => {
    switch (status) {
      case 'Activa': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Devuelta': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getEstadoActaColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'pendiente':
      case 'pendiente de firma':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pendiente de aprobación':
      case 'pendiente_de_aprobacion':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'firmada':
      case 'firmado':
      case 'firmada digitalmente':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'aprobada':
      case 'aprobado':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'rechazada':
      case 'rechazado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'anulada':
      case 'anulado':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getCategoriaIcon = (categoria) => {
    switch (categoria?.toLowerCase()) {
      case 'equipos':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'móviles':
      case 'moviles':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'monitores':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      default:
        return <HardDrive className="w-5 h-5" />;
    }
  };



  // Componente de paginación
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, filteredAsignaciones.length)}
              </span>{' '}
              de <span className="font-medium">{filteredAsignaciones.length}</span> resultados
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                    ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading || !actasMapaListo) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestión de Actas
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Gestiona actas de entrega y recepción
          </p>
        </div>

      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('asignaciones');
              setCurrentPage(1);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'asignaciones'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Gestión de Actas ({asignaciones.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('control-actas');
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'control-actas'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Control de Actas ({stats.actasSubidas})
          </button>
        </nav>
      </div>

      {/* Contenido de Asignaciones */}
      {activeTab === 'asignaciones' && (
        <>
          {/* Estadísticas Mejoradas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all ${filterStatus === 'Activa' && filterEstadoActa === 'todos'
                ? 'ring-2 ring-blue-500'
                : ''
                }`}
              onClick={() => {
                setFilterStatus('Activa');
                setFilterEstadoActa('todos');
                setSearchTerm('');
                setFilterCategoria('todos');
                setCurrentPage(1); // Resetear a primera página
              }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Asignaciones Activas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.asignacionesActivas}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">de {stats.totalAsignaciones} total</p>
                </div>
              </div>
            </div>

            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all ${filterEstadoActa === 'pendiente'
                ? 'ring-2 ring-orange-500'
                : ''
                }`}
              onClick={() => {
                setFilterEstadoActa('pendiente');
                setFilterStatus('todos');
                setSearchTerm('');
                setFilterCategoria('todos');
                setCurrentPage(1); // Resetear a primera página
              }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <FileCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actas Pendientes</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasPendientes}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">por revisar</p>
                </div>
              </div>
            </div>

            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all ${filterEstadoActa === 'aprobada'
                ? 'ring-2 ring-green-500'
                : ''
                }`}
              onClick={() => {
                setFilterEstadoActa('aprobada');
                setFilterStatus('todos');
                setSearchTerm('');
                setFilterCategoria('todos');
                setCurrentPage(1); // Resetear a primera página
              }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actas Aprobadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasAprobadas}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stats.porcentajeCompletado}% completado</p>
                </div>
              </div>
            </div>

            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all ${filterEstadoActa === 'rechazada'
                ? 'ring-2 ring-red-500'
                : ''
                }`}
              onClick={() => {
                setFilterEstadoActa('rechazada');
                setFilterStatus('todos');
                setSearchTerm('');
                setFilterCategoria('todos');
                setCurrentPage(1); // Resetear a primera página
              }}
            >
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actas Rechazadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasRechazadas}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">requieren atención</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros Avanzados */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="relative">
                    <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
                      <Search className="text-gray-400 w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar por código, categoría, usuario..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={filterEstadoActa}
                    onChange={(e) => setFilterEstadoActa(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="todos">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="aprobada">Aprobada</option>
                    <option value="firmada">Firmada</option>
                    <option value="rechazada">Rechazada</option>
                  </select>
                </div>
                <div>
                  <select
                    value={filterCategoria}
                    onChange={(e) => setFilterCategoria(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="todos">Todas las categorías</option>
                    <option value="Equipos">Equipos</option>
                    <option value="Insumos">Insumos</option>
                    <option value="Monitores">Monitores</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-4 shrink-0">
                {/* Toggle Vista Asignaciones */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700 shrink-0">
                  <button
                    onClick={() => setViewModeAsignaciones('list')}
                    className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewModeAsignaciones === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                    title="Vista de lista"
                  >
                    <ListIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewModeAsignaciones('grid')}
                    className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewModeAsignaciones === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                    title="Vista de cuadrícula"
                  >
                    <LayoutGrid className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-shrink-0">
                  <button
                    onClick={handleMarcarPendientesFirma}
                    disabled={loading || !asignacionesSinActa.length}
                    className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <FileCheck className="w-4 h-4" />
                    <span>Marcar Sin Acta como Pendientes</span>
                    {asignacionesSinActa.length > 0 && (
                      <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                        {asignacionesSinActa.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Asignaciones (Vista de Tarjetas / Lista) */}
          <div className="bg-transparent overflow-hidden">
            <div className={viewModeAsignaciones === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" : "flex flex-col gap-4"}>
              {currentAsignaciones.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <FileText className="w-12 h-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">No hay asignaciones</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-center max-w-sm mt-2">
                    No se encontraron resultados con los filtros actuales.
                  </p>
                </div>
              ) : (
                currentAsignaciones.map((asignacion) => {
                  const acta = buscarActaDeAsignacion(asignacion, actasPorAsignacion);

                  return (
                    <div
                      key={asignacion.id}
                      className={`bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col h-full relative overflow-hidden ${viewModeAsignaciones === 'list' ? 'sm:flex-row sm:items-center sm:gap-6' : ''}`}
                      onClick={() => handleRowClick(asignacion.id)}
                    >
                      {/* Decorative top border based on status */}
                      <div className={`absolute left-0 top-0 ${viewModeAsignaciones === 'list' ? 'h-full w-1' : 'w-full h-1'} ${!acta ? 'bg-orange-400' : 'bg-green-500'}`}></div>

                      <div className={`flex items-start justify-between mb-4 mt-1 ${viewModeAsignaciones === 'list' ? 'mb-0 mt-0 min-w-0 sm:w-1/3' : ''}`}>
                        <div className="flex items-center space-x-3">
                          <div className={`rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 ${viewModeAsignaciones === 'list' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                            {getCategoriaIcon(asignacion.activo?.categoria)}
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-1">
                              {asignacion.tipo === 'consumible' ? asignacion.activo?.nombre : asignacion.activo?.codigo}
                            </h3>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                              {asignacion.activo?.categoria === 'Consumibles' ? 'Insumos' : asignacion.activo?.categoria}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className={`flex-1 space-y-3 mb-5 ${viewModeAsignaciones === 'list' ? 'mb-0 space-y-0 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4' : ''}`}>
                        <div className={`flex items-center space-x-2 text-sm ${viewModeAsignaciones === 'list' ? 'sm:w-1/3' : ''}`}>
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-gray-900 dark:text-white font-medium truncate">
                              {asignacion.usuario?.nombre} {asignacion.usuario?.apellido}
                            </p>
                            {viewModeAsignaciones === 'grid' && (
                              <p className="text-gray-500 dark:text-gray-400 text-xs truncate">
                                {asignacion.usuario?.email}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={`flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300 ${viewModeAsignaciones === 'list' ? 'sm:w-1/4' : ''}`}>
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium">
                            {new Date(asignacion.fechaAsignacion).toLocaleDateString('es-ES', {
                              year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </span>
                        </div>

                        <div className={`bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 border border-gray-100 dark:border-gray-700/50 ${viewModeAsignaciones === 'list' ? 'p-1.5 border-none bg-transparent dark:bg-transparent' : ''}`}>
                          <div className="flex flex-col space-y-2">
                            {acta ? (
                              <div className={viewModeAsignaciones === 'list' ? 'flex items-center gap-2' : ''}>
                                <StatusBadge estado={acta.estado} />
                                {acta.metodoFirma && viewModeAsignaciones === 'grid' && (
                                  <div className="text-xs text-gray-500 mt-1.5 font-medium">
                                    Método: <span className="text-gray-700 dark:text-gray-300">{acta.metodoFirma.replace('_', ' ')}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center text-sm font-bold text-orange-600 dark:text-orange-400">
                                <AlertCircle className="w-4 h-4 mr-1.5" />
                                Sin acta
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className={`pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center gap-2 mt-auto ${viewModeAsignaciones === 'list' ? 'pt-0 border-t-0 mt-0 sm:ml-auto' : ''}`}>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate min-w-0 flex-1">
                          {acta?.subidoPor ? (
                            <span className="flex items-center gap-1.5 truncate" title={`Subido por: ${acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}`}>
                              <Upload className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                              <span className="truncate">
                                <span className="text-gray-400 font-normal">Subido por: </span>
                                <span className="font-semibold text-gray-700 dark:text-gray-300">
                                  {acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}
                                </span>
                              </span>
                            </span>
                          ) : (asignacion.asignadoPor || asignacion.asignado_por || asignacion.creadoPor) ? (
                            <span className="flex items-center gap-1.5 truncate" title={`Asignado por: ${asignacion.asignadoPor || asignacion.asignado_por || asignacion.creadoPor}`}>
                              <UserCheck className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                              <span className="truncate">
                                <span className="text-gray-400 font-normal">Asignado por: </span>
                                <span className="font-semibold text-gray-700 dark:text-gray-300">
                                  {asignacion.asignadoPor || asignacion.asignado_por || asignacion.creadoPor}
                                </span>
                              </span>
                            </span>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrevisualizarActa(acta, asignacion.id);
                            }}
                            className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 rounded-lg transition-colors"
                            title="Previsualizar acta"
                          >
                            <Eye className="w-5 h-5" />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerarActa(acta, asignacion.id);
                            }}
                            className="p-2 text-green-600 bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400 rounded-lg transition-colors"
                            title="Generar acta"
                          >
                            <FileText className="w-5 h-5" />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubirActa(acta, asignacion.id);
                            }}
                            className="p-2 text-purple-600 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 dark:text-purple-400 rounded-lg transition-colors"
                            title="Subir acta PDF"
                          >
                            <Upload className="w-5 h-5" />
                          </button>

                          {!acta && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarcarPendienteFirma(asignacion.id);
                              }}
                              className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 dark:text-orange-400 rounded-lg transition-colors"
                              title="Marcar como pendiente de firma"
                            >
                              <History className="w-5 h-5" />
                            </button>
                          )}

                          {asignacion.tipo === 'consumible' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMovimiento(asignacion.id);
                              }}
                              className="p-2 text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 rounded-lg transition-colors ml-auto"
                              title="Eliminar asignación"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={paginate}
              />
            )}
          </div>
        </>
      )}

      {/* Contenido de Control de Actas */}
      {activeTab === 'control-actas' && (
        <>
          {/* Header de Control de Actas */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Control de Actas
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Gestiona las actas de todos los usuarios
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowHistoricoModal(true)}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                title="Subir acta PDF de empleados antiguos o actuales"
              >
                <Upload className="w-5 h-5" />
                <span>Subir Acta Histórica</span>
              </button>
            </div>
          </div>

          {/* Estadísticas de Control de Actas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Actas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{actas.length}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">documentos</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <FileCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pendientes</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasPendientes}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">por revisar</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aprobadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasAprobadas}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">completadas</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rechazadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.actasRechazadas}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">requieren atención</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros de Control de Actas */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
                    <Search className="text-gray-400 w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre de archivo o usuario..."
                    value={searchTermActas}
                    onChange={(e) => setSearchTermActas(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filterStatusActas}
                  onChange={(e) => setFilterStatusActas(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="todos">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="firmado">Firmado</option>
                  <option value="rechazado">Rechazado</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <select
                  value={sortOrderActas}
                  onChange={(e) => setSortOrderActas(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fecha-desc">Más recientes primero</option>
                  <option value="fecha-asc">Más antiguos primero</option>
                  <option value="usuario">Por usuario</option>
                  <option value="activo">Por activo</option>
                  <option value="estado">Por estado</option>
                </select>
              </div>

              {/* Toggle Vista Control Actas */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700 shrink-0">
                <button
                  onClick={() => setViewModeControl('list')}
                  className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewModeControl === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  title="Vista de lista"
                >
                  <ListIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewModeControl('grid')}
                  className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewModeControl === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  title="Vista de cuadrícula"
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Lista de Actas (Vista de Tarjetas) */}
          <div className="bg-transparent overflow-hidden">
            {loadingActas ? (
              <div className="flex items-center justify-center p-12 h-64 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className={viewModeControl === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "flex flex-col gap-4"}>
                {filteredActas.length === 0 ? (
                  <div className="col-span-full flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <FileText className="w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">No hay actas registradas</h3>
                  </div>
                ) : (
                  filteredActas.map((acta) => {
                    const isHistorica = acta.esHistorica;
                    const isConsumable = !!acta.movimientoId;

                    let usuario = acta.asignacion?.usuario || acta.movimiento?.usuario;
                    if (isHistorica) {
                      usuario = acta.usuario || { nombre: acta.usuarioNombre, apellido: '' };
                    }

                    let item = acta.asignacion?.activo || acta.movimiento?.consumible;
                    if (isHistorica) {
                      item = acta.activo || { codigo: 'Sin equipo', categoria: 'Histórico' };
                    }

                    return (
                      <div
                        key={acta.id}
                        className={`bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col h-full relative ${viewModeControl === 'list' ? 'sm:flex-row sm:items-center sm:gap-6' : ''}`}
                        onClick={() => {
                          if (isHistorica) {
                            handlePreview(acta.id, acta.nombreArchivo);
                          } else {
                            const targetId = acta.asignacionId ? acta.asignacionId : `mov_${acta.movimientoId}`;
                            handleRowClick(targetId);
                          }
                        }}
                      >
                        {/* Decorative status indicator */}
                        <div className={`absolute left-0 top-0 ${viewModeControl === 'list' ? 'h-full w-1' : 'w-full h-1'} ${acta.estado === 'pendiente' ? 'bg-orange-400' : acta.estado === 'rechazado' ? 'bg-red-500' : 'bg-green-500'}`}></div>

                        <div className={`flex items-start justify-between mb-4 ${viewModeControl === 'list' ? 'mb-0 min-w-0 sm:w-1/3' : ''}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 ${viewModeControl === 'list' ? 'w-10 h-10' : 'w-10 h-10'}`}>
                              <FileText className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1 flex items-center gap-2">
                                {acta.nombreArchivo}
                                {isHistorica && (
                                  <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase flex-shrink-0">
                                    Histórica
                                  </span>
                                )}
                              </div>
                              {acta.descripcion && viewModeControl === 'grid' && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {acta.descripcion}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className={`flex-1 space-y-3 mb-5 ${viewModeControl === 'list' ? 'mb-0 space-y-0 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4' : ''}`}>
                          <div className={`flex items-center space-x-2 text-sm ${viewModeControl === 'list' ? 'sm:w-1/3' : ''}`}>
                            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-900 dark:text-white font-medium truncate">
                              {usuario?.nombre} {usuario?.apellido}
                            </span>
                          </div>

                          <div className={`flex items-center space-x-2 text-sm ${viewModeControl === 'list' ? 'sm:w-1/3' : ''}`}>
                            <HardDrive className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300 font-medium truncate">
                              {isConsumable ? item?.nombre : item?.codigo} <span className="text-xs text-gray-400">({item?.categoria})</span>
                            </span>
                          </div>

                          <div className={`flex items-center justify-between ${viewModeControl === 'list' ? 'sm:w-1/4' : ''}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-md ${getEstadoActaColor(acta.estado)}`}>
                              {acta.estado}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              {acta.fechaFirma ? new Date(acta.fechaFirma).toLocaleDateString('es-ES') : 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className={`pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center space-x-2 mt-auto ${viewModeControl === 'list' ? 'pt-0 border-t-0 mt-0 sm:ml-auto' : ''}`}>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate min-w-0 flex-1">
                            {acta.subidoPor && (
                              <span className="flex items-center gap-1.5 truncate" title={`Subido por: ${acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}`}>
                                <Upload className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                <span className="truncate">
                                  <span className="text-gray-400 font-normal">Subido por: </span>
                                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                                    {acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}
                                  </span>
                                </span>
                              </span>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(acta.id, acta.nombreArchivo);
                              }}
                              className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 rounded-lg transition-colors"
                              title="Previsualizar archivo"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(acta.id);
                              }}
                              className="p-2 text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 rounded-lg transition-colors"
                              title="Eliminar registro"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Elegir la asignación para generar una acta nueva (desde el menú lateral) */}
      <SeleccionarAsignacionActaModal
        isOpen={showSelectorActa}
        onClose={() => setShowSelectorActa(false)}
        asignaciones={asignaciones}
        actasPorAsignacion={actasPorAsignacion}
        onSelect={(a) => {
          setShowSelectorActa(false);
          setSelectedAsignacion(a);
          setShowGenerarActaModal(true);
        }}
      />

      {/* Modal de generar acta */}
      <GenerarActaModal
        isOpen={showGenerarActaModal}
        onClose={handleCloseGenerarActa}
        asignacion={selectedAsignacion}
        onSuccess={handleGenerarActaSuccess}
      />

      {/* Modal de subida de acta por admin */}
      {showUploadModal && createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModal(false);
            }
          }}
        >
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Subir Acta por Usuario
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Asignación Seleccionada
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    {selectedAsignacionForUpload ? (
                      <div>
                        <div className="font-medium">
                          {selectedAsignacionForUpload.activo?.codigo} - {selectedAsignacionForUpload.usuario?.nombre} {selectedAsignacionForUpload.usuario?.apellido}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedAsignacionForUpload.usuario?.email}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Ninguna asignación seleccionada</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Seleccionar archivo PDF
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setSelectedFile(null);
                      setSelectedAsignacionForUpload(null);
                      setObservaciones('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUploadActa}
                    disabled={!selectedFile || !selectedAsignacionForUpload || uploading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? 'Subiendo...' : 'Subir'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de previsualización personalizada */}
      {showPrevisualizacionModal && createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPrevisualizacionModal(false);
            }
          }}
        >
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Previsualización Personalizada
              </h3>
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Personaliza la previsualización del acta en estado pendiente de firma.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Incluir firma de TI
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={incluirFirmaTI}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setIncluirFirmaTI(newValue);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Mostrar firma de TI en el documento
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha de entrega
                  </label>
                  <input
                    type="date"
                    value={fechaEntrega}
                    onChange={(e) => setFechaEntrega(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowPrevisualizacionModal(false);
                      setSelectedActaForPreview(null);
                      setSelectedAsignacionForPreview(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmarPrevisualizacion}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
                  >
                    Previsualizar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Subida para Control de Actas */}
      {showUploadModalActas && createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModalActas(false);
            }
          }}
        >
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Subir Acta
              </h3>
              <form onSubmit={handleUploadActas} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Usuario ID
                  </label>
                  <input
                    type="number"
                    value={uploadDataActas.usuarioId}
                    onChange={(e) => setUploadDataActas({ ...uploadDataActas, usuarioId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre del Archivo
                  </label>
                  <input
                    type="text"
                    value={uploadDataActas.nombreArchivo}
                    onChange={(e) => setUploadDataActas({ ...uploadDataActas, nombreArchivo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo PDF
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChangeActas}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={uploadDataActas.descripcion}
                    onChange={(e) => setUploadDataActas({ ...uploadDataActas, descripcion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadModalActas(false);
                      setUploadDataActas({ usuarioId: '', nombreArchivo: '', descripcion: '' });
                      setSelectedFileActas(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Subir
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Histórico (Usando createPortal interno en el componente o de forma normal) */}
      <UploadHistoricoModal
        isOpen={showHistoricoModal}
        onClose={() => setShowHistoricoModal(false)}
        onUploadComplete={() => {
          setShowHistoricoModal(false);
          fetchActas(); // Recargar la lista de actas tras subir
          showToast('Éxito', 'Acta histórica subida correctamente', 'success');
        }}
      />

      {/* Signature Drawer para usuarios/admins sin firma digital */}
      <SignatureDrawer
        isOpen={showSignatureDrawer}
        onClose={() => setShowSignatureDrawer(false)}
        onSave={handleSaveSignatureFromDrawer}
        title="Crear tu firma digital para actas"
      />
    </div>
  );
};

export default GestionActas; 